import { FC, useCallback } from 'react';
import { Flex, Icon } from '@chakra-ui/react';
import styled from '@emotion/styled/macro';
import { mq } from 'styles/breakpoints';
import { Button } from 'components';
import { ArrowUp } from 'components/Icons/Icons';
import { isCorporateThemeSelector } from 'modules/app/appSlice';
import { useSelector } from 'react-redux';

const Wrapper = styled(Flex)`
  flex-direction: column;
  width: 100%;

  h2 {
    margin-bottom: 10px;
  }

  h3,
  h4,
  h5,
  h6 {
    font-weight: bold;
    line-height: 160%;
    margin-bottom: 10px;
    margin-top: 30px;
  }

  strong {
    font-weight: bold;
  }

  p {
    font-weight: 500;
    line-height: 160%;
    margin: 0 0 25px 0;

    &:last-of-type {
      margin-bottom: 0;
    }
  }

  ul,
  ol {
    padding-left: 12px;
    margin-top: 50px;
  }

  li {
    padding-left: 15px;
    margin-bottom: 20px;
    font-weight: 500;
  }
`;

const IconButton = styled(Button)`
  border-radius: 9px;
  height: 45px;
  min-width: 45px;
  max-width: 45px;
  transition: all 0.3s linear;
  position: absolute;
  right: 56px;
  bottom: 60px;

  ${mq.xl} {
    right: 96px;
    bottom: 66px;
  }
`;

interface Props {
  showButton?: boolean;
}

const PolicyPage: FC<Props> = ({ showButton, children }) => {
  const isCorporateTheme = useSelector(isCorporateThemeSelector);

  const goToTheTop = useCallback((): void => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Wrapper
      sx={{
        '& h1, & h2, & h3, & h4, & h5, & h6': { fontFamily: 'heading', color: 'secondary.800' },
        '& li': { fontFamily: 'body', color: 'secondary.700', fontSize: isCorporateTheme ? 'md' : 'mdMax' },
        '& p': { color: 'secondary.700', fontSize: isCorporateTheme ? 'md' : 'mdMax' },
        '& h2': { fontSize: isCorporateTheme ? '2xl' : '4xl', lineHeight: isCorporateTheme ? '24px' : '37px' },
        '& h3, & h4, & h5, & h6': { fontSize: isCorporateTheme ? 'lg' : 'mdMax' },
      }}
    >
      {children}
      {showButton && (
        <IconButton onClick={goToTheTop} bg="other.CTA" _hover={{ bg: 'other.accent' }} sx={{ '& path': { fill: 'white' } }} aria-hidden="true">
          <Icon as={ArrowUp} boxSize={5} />
        </IconButton>
      )}
    </Wrapper>
  );
};

export default PolicyPage;
